import { Schema } from "effect";
import { CardState, PositiveInt } from "../../srs/types.ts";
import { Settings } from "./settings.ts";

export const Kind = Schema.Literals(["meaning", "pinyin", "recall"]);
export type Kind = typeof Kind.Type;

export const Card = Schema.Struct({
  prompt: Schema.String,
  kind: Kind,
  answer: Schema.String,
  accepted: Schema.optionalKey(Schema.Array(Schema.String)),
  set: Schema.String,
  level: Schema.Number,
  word: Schema.optionalKey(Schema.String),
  pos: Schema.optionalKey(Schema.String),
  pinyin: Schema.optionalKey(Schema.String),
  say: Schema.optionalKey(Schema.String),
});
export type Card = typeof Card.Type;

export const CardId = Schema.Int.check(Schema.isGreaterThan(0)).pipe(Schema.brand("CardId"));
export type CardId = typeof CardId.Type;

export const SetId = Schema.Int.check(Schema.isGreaterThan(0)).pipe(Schema.brand("SetId"));
export type SetId = typeof SetId.Type;

export const Rev = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1)).pipe(Schema.brand("Rev"));
export type Rev = typeof Rev.Type;

export const ReviewId = Schema.String.check(Schema.isUUID(4)).pipe(Schema.brand("ReviewId"));
export type ReviewId = typeof ReviewId.Type;

export const ANSWER_MAX = 200;

export const Answer = Schema.String.check(
  Schema.isTrimmed(),
  Schema.makeFilter((s: string) => s === s.normalize("NFC") || "an answer must be NFC normalised"),
  Schema.makeFilter((s: string) => {
    const n = [...s].length;
    return (n >= 1 && n <= ANSWER_MAX) || `an answer has 1 to ${ANSWER_MAX} characters, not ${n}`;
  }),
).pipe(Schema.brand("Answer"));
export type Answer = typeof Answer.Type;

export const FlashcardSet = Schema.Struct({
  id: SetId,
  slug: Schema.String,
  name: Schema.String,
  description: Schema.NullOr(Schema.String),
  settings: Settings,
  settingsRev: Rev,
});
export type FlashcardSet = typeof FlashcardSet.Type;

export const CardContent = Schema.Struct({
  id: CardId,
  setId: SetId,
  externalId: Schema.String,
  kind: Schema.NullOr(Schema.String),
  // TODO: Decode nonblank prompt/answer here; the row codec currently accepts cards that cannot be studied.
  prompt: Schema.String,
  answer: Schema.String,
  accepted: Schema.Array(Schema.String),
  rejected: Schema.Array(Schema.String),
  mnemonic: Schema.NullOr(Schema.String),
  notes: Schema.NullOr(Schema.String),
  level: PositiveInt,
  position: Schema.Int,
  fields: Schema.Record(Schema.String, Schema.Unknown),
  contentRev: Rev,
});
export type CardContent = typeof CardContent.Type;

export const Flashcard = Schema.Struct({ ...CardContent.fields, state: CardState });
export type Flashcard = typeof Flashcard.Type;

export const Prerequisite = Schema.Struct({ cardId: CardId, requiresCardId: CardId });
export type Prerequisite = typeof Prerequisite.Type;
